import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import EnhancedTable from 'components/enhanced/table/EnhancedTable';
import { RowAction } from 'components/enhanced/types/enhanced.types';
import { ToolbarAction, ToolbarFilter } from 'components/enhanced/types/toolbar.types';
import { IEquipment } from 'features/inventory';
import { TenantPage } from 'layouts';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { equipmentService } from 'shared/services';
import { equipmentCheckListTemplateService } from '../api/checklist-template.service';
import { ChecklistHistoryDetailsDialog } from '../components/ChecklistTemplateUpdateDialog';
import { TemplateChecklistColumns } from '../hooks/columns';
import { IChecklistTemplate } from '../types';

export const ChecklistTemplatesPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tableRefreshRef = useRef<(() => void) | null>(null);

  const [open, setOpenDialog] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<IChecklistTemplate | null>(null);

  const [checklists, setChecklists] = useState<IChecklistTemplate[]>([]);
  const [instrumentOptions, setInstrumentOptions] = useState<IEquipment[]>([]);

  const columns = TemplateChecklistColumns();

  useEffect(() => {
    equipmentCheckListTemplateService
      .getAllPaginated()
      .then((res) => {
        setChecklists(res?.results || res?.data || (Array.isArray(res) ? res : []));
      })
      .catch(() => {
        setChecklists([]);
      });

    equipmentService
      .getAllBaseInformation()
      .then((res) => {
        setInstrumentOptions(res?.results || res?.data || (Array.isArray(res) ? res : []));
      })
      .catch(() => {
        setInstrumentOptions([]);
      });
  }, []);

  const handleDuplicate = (row: IChecklistTemplate) => {
    navigate('/tenant/equipments/checklist/new/', {
      state: {
        isDuplicate: true,
        duplicateData: row
      }
    });
  };

  const handleEdit = (row: IChecklistTemplate) => {
    setSelectedTemplate(row);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTemplate(null);
  };

  const rowActions: RowAction[] = [
    {
      id: 'edit',
      label: t('actions.edit'),
      icon: <EditIcon fontSize="small" />,
      onClick: (row: IChecklistTemplate) => handleEdit(row)
    },
    {
      id: 'duplicate',
      label: t('actions.duplicate'),
      icon: <ContentCopyIcon fontSize="small" />,
      onClick: (row: IChecklistTemplate) => handleDuplicate(row)
    }
  ];

  const toolbarActions: ToolbarAction[] = [
    {
      id: 'add-btn',
      actionType: 'button',
      variant: 'contained',
      label: 'Nuova checklist',
      startIcon: <AddIcon />,
      onClick: () => navigate('/tenant/equipments/checklist/new/')
    }
  ];

  const filtersConfig = useMemo<ToolbarFilter[]>(() => {
    const formattedChecklistOptions = checklists.map((item) => ({
      value: item.id,
      label: item.name
    }));

    const formattedInstrumentOptions = instrumentOptions.map((item) => ({
      value: item.id,
      label: item.description || item.code || '-'
    }));

    return [
      {
        id: 'id',
        type: 'select',
        label: 'Checklist',
        options: formattedChecklistOptions
      },
      {
        id: 'equipment',
        type: 'select',
        label: 'Strumento',
        options: formattedInstrumentOptions
      }
    ];
  }, [checklists, instrumentOptions]);

  return (
    <TenantPage title={'Checklists'}>
      <EnhancedTable
        service={equipmentCheckListTemplateService}
        columns={columns}
        refresh={tableRefreshRef}
        rowActions={rowActions}
        toolbarFilters={filtersConfig}
        toolbarActions={toolbarActions}
        variant="cards"
      />
      <ChecklistHistoryDetailsDialog
        open={open}
        onClose={handleCloseDialog}
        instanceData={selectedTemplate}
      />
    </TenantPage>
  );
};
